import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Hairline,
  Select,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type {
  CompanyDetailDTO,
  CompanyLocationDTO,
  CustomerDetailDTO,
} from "@wmart/shared-types";

interface Props {
  customer: CustomerDetailDTO;
  onSuccess: () => void | Promise<void>;
}

/**
 * Manage the customer's M:N grants to company_locations.
 *
 * Top half: the existing allowed_locations with a "Remove" button per row →
 * DELETE /v1/admin/customers/:customerId/locations/:locId.
 *
 * Bottom half: a dropdown of the parent company's locations that are NOT
 * yet allowed (set difference) plus an "Add" button → POST
 * /v1/admin/customers/:customerId/locations with `{company_location_id}`.
 *
 * `customer.company_id` should never be null for an approved customer (the
 * approve flow requires a company), but the renderer guards defensively.
 */
export function AllowedLocationsEditor({ customer, onSuccess }: Props) {
  const { notify, notifyProblem } = useToast();
  const [companyLocations, setCompanyLocations] = useState<
    CompanyLocationDTO[]
  >([]);
  const [companyLoadFailed, setCompanyLoadFailed] = useState(false);
  const [selectedToAdd, setSelectedToAdd] = useState("");
  const [busy, setBusy] = useState(false);

  // Fetch the parent company's full location set so we can render the
  // "not-yet-assigned" dropdown.
  useEffect(() => {
    if (!customer.company_id) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await api.get<CompanyDetailDTO>(
          `/v1/admin/companies/${customer.company_id}`,
        );
        if (!cancelled) {
          setCompanyLocations(detail.locations);
          setCompanyLoadFailed(false);
        }
      } catch (err) {
        if (cancelled) return;
        setCompanyLoadFailed(true);
        if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
        else notify("error", "Failed to load company locations");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customer.company_id, notify, notifyProblem]);

  if (!customer.company_id) {
    return (
      <Card as="section" className="p-6">
        <h2 className="font-serif text-title-lg text-ink-900">
          Allowed locations
        </h2>
        <p className="mt-2 text-body-sm text-ink-500">
          Customer has no company assigned.
        </p>
      </Card>
    );
  }

  const allowedIds = new Set(customer.allowed_locations.map((l) => l.id));
  const candidates = companyLocations.filter((l) => !allowedIds.has(l.id));

  async function addLocation() {
    if (!selectedToAdd) return;
    setBusy(true);
    try {
      await api.post(`/v1/admin/customers/${customer.id}/locations`, {
        company_location_id: selectedToAdd,
      });
      notify("success", "Location added");
      setSelectedToAdd("");
      await onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Add failed");
    } finally {
      setBusy(false);
    }
  }

  async function removeLocation(locId: string) {
    if (!confirm("Remove this location from the customer's allowed list?")) {
      return;
    }
    setBusy(true);
    try {
      await api.delete(
        `/v1/admin/customers/${customer.id}/locations/${locId}`,
      );
      notify("success", "Location removed");
      await onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Remove failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" className="p-6">
      <h2 className="font-serif text-title-lg text-ink-900">
        Allowed locations
      </h2>

      <div className="mt-4">
        {customer.allowed_locations.length === 0 ? (
          <p className="py-2 text-center text-body-sm text-ink-500">
            No allowed locations yet.
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>City</TH>
                <TH>Postcode</TH>
                <TH className="text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {customer.allowed_locations.map((l) => (
                <TR key={l.id}>
                  <TD>{l.name}</TD>
                  <TD>{l.address.city}</TD>
                  <TD className="font-mono text-body-sm">
                    {l.address.postcode}
                  </TD>
                  <TD className="text-right">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => void removeLocation(l.id)}
                      disabled={busy}
                      className="text-danger"
                    >
                      Remove
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      <Hairline className="my-4" />

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Select
            label="Add location"
            value={selectedToAdd}
            onChange={(e) => setSelectedToAdd(e.target.value)}
            disabled={busy || candidates.length === 0 || companyLoadFailed}
          >
            <option value="">
              {companyLoadFailed
                ? "Failed to load company locations"
                : candidates.length === 0
                  ? "All company locations already assigned"
                  : "Select a location to add…"}
            </option>
            {candidates.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} — {l.address.city}, {l.address.postcode}
              </option>
            ))}
          </Select>
        </div>
        <Button
          onClick={() => void addLocation()}
          disabled={busy || !selectedToAdd}
        >
          Add
        </Button>
      </div>
    </Card>
  );
}
